// Client/src/index.js
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "./styles.css";
import App from "./App";
import ErrorBoundary from "./ErrorBoundary";
import PartnerWorkspace from "./partner/PartnerWorkspace";
import PartnerAdmin from "./partner/PartnerAdmin";

console.log("🔥 INDEX VERSION 3 LOADED");

const ACCESS_KEY = "tvac_access_granted";
const ACCESS_EMAIL_KEY = "tvac_access_email";

// NOTE: This UI is hosted separately from the API. For the access check we call the API directly.
// If you change the API host, update this constant.
const API_BASE = "https://tokenlex-api-new.onrender.com";

function getApiReportRenderTokenFromLocation() {
  try {
    const url = new URL(window.location.href);
    const queryToken =
      url.searchParams.get("tvac_api_render_token") ||
      url.searchParams.get("apiReportRenderToken") ||
      "";

    if (queryToken) return queryToken.trim();

    const match = window.location.pathname.match(/^\/api-report-render\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1] || "").trim() : "";
  } catch {
    return "";
  }
}

function getWorkspaceMode() {
  try {
    return String(new URL(window.location.href).searchParams.get("workspace") || "")
      .trim()
      .toLowerCase();
  } catch {
    return "";
  }
}

function Root() {
  const apiReportRenderToken = getApiReportRenderTokenFromLocation();
  const apiReportRenderMode = Boolean(apiReportRenderToken);
  const workspaceMode = getWorkspaceMode();
  const partnerWorkspaceMode = workspaceMode === "partner";
  const partnerAdminMode = workspaceMode === "partner-admin";

  const [hasAccess, setHasAccess] = useState(false);
  const [checkedStorage, setCheckedStorage] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  // The API/PDF render, Partner Workspace and Partner Admin routes must bypass
  // the public customer localStorage gate entirely.
  useEffect(() => {
    if (apiReportRenderMode || partnerWorkspaceMode || partnerAdminMode) {
      setCheckedStorage(true);
      return;
    }

    try {
      const stored = window.localStorage.getItem(ACCESS_KEY);
      const storedEmail = window.localStorage.getItem(ACCESS_EMAIL_KEY);

      // Require both flag AND stored email.
      if (stored === "1" && storedEmail) {
        setHasAccess(true);
      }
    } catch (e) {
      // Ignore localStorage errors – we just fall back to the gate.
    } finally {
      setCheckedStorage(true);
    }
  }, [apiReportRenderMode, partnerWorkspaceMode, partnerAdminMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    const normalizedEmail = String(email).trim().toLowerCase();

    // Very light email sanity check (MVP).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setChecking(true);

    try {
      const resp = await fetch(`${API_BASE}/api/access/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.ok && data && data.allowed) {
        try {
          window.localStorage.setItem(ACCESS_KEY, "1");
          window.localStorage.setItem(ACCESS_EMAIL_KEY, normalizedEmail);
        } catch (e) {
          // If localStorage fails we simply do not persist access.
        }
        setHasAccess(true);
      } else {
        setError(
          "No access found for this email. Please use the same email as in checkout, or contact michael@tvacai.com."
        );
      }
    } catch (err) {
      setError(
        "Could not verify access right now. Please try again in a moment, or contact michael@tvacai.com."
      );
    } finally {
      setChecking(false);
    }
  };

  if (!checkedStorage) return null;

  // Keep the dedicated Puppeteer/frontend PDF route highest priority.
  if (apiReportRenderMode) {
    return (
      <ErrorBoundary>
        <App
          apiReportRenderMode={true}
          apiReportRenderToken={apiReportRenderToken}
        />
      </ErrorBoundary>
    );
  }

  // Partner Admin is deliberately separate from the public customer gate.
  // Its own component handles secure staff authentication and authorization.
  if (partnerAdminMode) {
    return <PartnerAdmin apiBase={API_BASE} />;
  }

  // Partner Workspace is deliberately separate from the public customer gate
  // and never uses a Partner API key in the browser.
  if (partnerWorkspaceMode) {
    return <PartnerWorkspace apiBase={API_BASE} />;
  }

  if (hasAccess) {
    return (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    );
  }

  // Public customer access gate.
  return (
    <div className="gate-shell">
      <div className="gate-shell-inner">
        <div className="gate-card">
          <div className="gate-logo-pill">
            <span>TVAC</span>
          </div>

          <h1 className="gate-title">Access to TVAC</h1>
          <p className="gate-tagline">
            This version of the Tokenization Value-Add Calculator (TVAC) is
            protected by email-based access.
          </p>
          <p className="gate-hint">
            Access is tied to the email used at checkout and must not be shared.
            If you believe you should have access but do not, please contact{" "}
            <a href="mailto:michael@tvacai.com">michael@tvacai.com</a>.
          </p>

          <form className="gate-input-row" onSubmit={handleSubmit}>
            <label className="gate-label" htmlFor="tvac-access-email">
              Email
            </label>
            <input
              id="tvac-access-email"
              type="email"
              className="gate-input"
              placeholder="Enter your email used for purchase"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className="gate-actions">
              <button
                type="submit"
                className="gate-btn"
                disabled={checking || !email.trim()}
              >
                {checking && <span className="spinner" aria-hidden="true" />}
                {checking ? "Checking…" : "Continue to TVAC"}
              </button>
              <span className="gate-secondary-text">
                Please do not share this link or someone else’s email.
              </span>
            </div>
          </form>

          {error && <div className="gate-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element with id 'root' not found in index.html");
}

const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
